import React, { useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Card, Chip, Container, Divider, Grid,
  IconButton, List, ListItem, ListItemAvatar, ListItemText, Paper, Stack,
  Tab, Tabs, Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import PetsRoundedIcon from '@mui/icons-material/PetsRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';

const nlLong = new Intl.DateTimeFormat('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const nlShort = new Intl.DateTimeFormat('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });

const demoDate = new Date(2026, 8, 25);

const arrivals = [
  { name: 'Yara', detail: 'Logeren · vanaf 14.00 uur' },
  { name: 'Pepper', detail: 'Dagopvang · 08.00–18.00 uur' },
];
const staying = [
  { name: 'Jet', detail: 'Logeert · vertrek maandag 28 september vóór 10.00 uur', special: true },
  { name: 'Marley', detail: 'Logeert · vertrek zondag 27 september vóór 10.00 uur', special: true },
];
const departures = [{ name: 'Riley', detail: 'Ophalen vóór 10.00 uur' }];

const agenda = [
  { d: new Date(2026, 8, 25), dogs: ['Jet', 'Marley', 'Yara'], note: '3 van 3' },
  { d: new Date(2026, 8, 26), dogs: ['Jet', 'Marley', 'Yara'], note: '3 van 3' },
  { d: new Date(2026, 8, 27), dogs: ['Jet', 'Marley'], note: '2 van 3' },
  { d: new Date(2026, 8, 28), dogs: ['Jet'], note: '1 van 3' },
  { d: new Date(2026, 8, 29), dogs: ['Pepper'], note: 'dagopvang' },
  { d: new Date(2026, 8, 30), dogs: [], note: 'vrij' },
  { d: new Date(2026, 9, 1), dogs: ['Jacky', 'Olle'], note: '2 van 3' },
];

function DogList({ items, empty = 'Geen honden' }: { items: any[]; empty?: string }) {
  if (!items.length) return <Typography color="text.secondary">{empty}</Typography>;
  return <List disablePadding>{items.map((item) => (
    <ListItem key={item.name} disableGutters sx={{ py: 0.75 }}>
      <ListItemAvatar><Avatar sx={{ bgcolor: 'secondary.light', color: 'text.primary' }}><PetsRoundedIcon /></Avatar></ListItemAvatar>
      <ListItemText primary={<Stack direction="row" spacing={1} alignItems="center"><b>{item.name}</b>{item.special && <Chip size="small" label="uitzonderingshond" />}</Stack>} secondary={item.detail} />
    </ListItem>
  ))}</List>;
}

export const AdminDashboardPage = () => {
  const [tab, setTab] = useState(0);
  const dateTitle = useMemo(() => nlLong.format(demoDate), []);
  const nav = [
    ['Vandaag', <WbSunnyRoundedIcon />], ['Agenda', <CalendarMonthRoundedIcon />], ['Honden', <PetsRoundedIcon />],
    ['Klanten', <GroupsRoundedIcon />], ['Aanvragen', <AssignmentRoundedIcon />], ['Wachtlijst', <NotificationsActiveRoundedIcon />], ['Instellingen', <TuneRoundedIcon />]
  ];

  return <Box sx={{ minHeight: '100vh', bgcolor: '#fffafb', py: { xs: 2, md: 4 } }}>
    <Container maxWidth="xl">
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} alignItems={{ md: 'center' }}>
        <Box>
          <Typography variant="overline" color="text.secondary">Pippaya Hondenopvang · beheer</Typography>
          <Typography variant="h3" sx={{ textTransform: 'capitalize' }}>{dateTitle}</Typography>
          <Typography color="text.secondary">In één oogopslag wie er is, komt en naar huis gaat.</Typography>
        </Box>
        <Button variant="contained" size="large" startIcon={<AddRoundedIcon />}>Reservering toevoegen</Button>
      </Stack>

      <Paper sx={{ mt: 3, overflowX: 'auto' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {nav.map(([label, icon]: any) => <Tab key={label} icon={icon} iconPosition="start" label={label} />)}
        </Tabs>
      </Paper>

      {tab === 0 && <>
        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={4}><Card sx={{ p: 2.5, height: '100%' }}><Stack direction="row" spacing={1} alignItems="center"><PetsRoundedIcon /><Typography variant="h5">Aanwezig / logeren</Typography></Stack><Divider sx={{ my: 1.5 }} /><DogList items={staying} /></Card></Grid>
          <Grid item xs={12} md={4}><Card sx={{ p: 2.5, height: '100%' }}><Stack direction="row" spacing={1} alignItems="center"><LoginRoundedIcon /><Typography variant="h5">Komt vandaag</Typography></Stack><Divider sx={{ my: 1.5 }} /><DogList items={arrivals} /></Card></Grid>
          <Grid item xs={12} md={4}><Card sx={{ p: 2.5, height: '100%' }}><Stack direction="row" spacing={1} alignItems="center"><LogoutRoundedIcon /><Typography variant="h5">Gaat vandaag naar huis</Typography></Stack><Divider sx={{ my: 1.5 }} /><DogList items={departures} /></Card></Grid>
        </Grid>

        <Grid container spacing={2.5} sx={{ mt: 0.25 }}>
          <Grid item xs={12} lg={8}>
            <Card sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box><Typography variant="h5">Komende dagen</Typography><Typography color="text.secondary">Volledige weekdag en datum blijven altijd zichtbaar.</Typography></Box>
                <Stack direction="row"><IconButton><ArrowBackIosNewRoundedIcon fontSize="small" /></IconButton><Button>Vandaag</Button><IconButton><ArrowForwardIosRoundedIcon fontSize="small" /></IconButton></Stack>
              </Stack>
              <Grid container spacing={1.25}>{agenda.map((day) => <Grid item xs={12} sm={6} md key={day.d.toISOString()}><Paper variant="outlined" sx={{ p: 1.5, minHeight: 150, boxShadow: 'none', borderColor: 'secondary.light' }}><Typography fontWeight={800} sx={{ textTransform: 'capitalize' }}>{nlShort.format(day.d)}</Typography><Chip size="small" label={day.note} sx={{ my: 1 }} />{day.dogs.map(x => <Typography key={x} variant="body2">🐾 {x}</Typography>)}{!day.dogs.length && <Typography variant="body2" color="text.secondary">Geen reserveringen</Typography>}</Paper></Grid>)}</Grid>
            </Card>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Stack spacing={2}>
              <Alert severity="info"><b>Optie loopt af</b><br />Een optie vervalt zaterdag 26 september 2026 om 14.30 uur.</Alert>
              <Alert severity="warning"><b>Wachtlijst</b><br />Er staat 1 aanvraag op de wachtlijst voor een periode met een optie.</Alert>
              <Alert severity="success"><b>Betalingen</b><br />Geen openstaande betaling die vandaag actie vraagt.</Alert>
            </Stack>
          </Grid>
        </Grid>
      </>}

      {tab !== 0 && <Card sx={{ mt: 3, p: 4 }}><Typography variant="h4">{nav[tab][0]}</Typography><Typography color="text.secondary" sx={{ mt: 1 }}>Dit scherm bouwen we als volgende onderdeel. De navigatie en beheerstructuur staan al klaar.</Typography></Card>}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 3 }}>Werkprototype · gebruikt nog fictieve gegevens en is nog niet gekoppeld aan de live Supabase-database.</Typography>
    </Container>
  </Box>;
};
